import { useEffect, useRef } from 'react';

import { ResourceKeyBrowser } from 'pages/lti_resource_keys/components/ResourceKeyBrowser';
import GMetrixDialogShell from 'components/GMetrixDialogShell/GMetrixDialogShell';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { ConsumerSettings, PartnerApi } from 'apis/PartnerApi';
import { helpContent } from 'pages/lti_resource_keys/help';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { PageHeader } from 'components/layout/page';
import { DefaultLayout } from 'components/layout';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

export const LTIResourceKeys = () => {

    const { highestRole, selectedPartner } = useApp();
    const { createToast } = useNotification();
    const { formatMessage } = useIntl();
    const partnerId = selectedPartner?.id ?? highestRole?.partnerId;
    const credentialsDialogRef = useRef<any>();

    const [state, setState] = useSuperState({
        consumerSettings: null as ConsumerSettings | null,
        loadingSettings: true,
        requestingKey: false
    });

    const { consumerSettings, loadingSettings, requestingKey } = state;

    const copyToClipboard = async (value: string) => {

        try {

            await navigator.clipboard.writeText(value);
            createToast(formatMessage({ id: 'copied_to_clipboard.7b65054c', defaultMessage: 'Copied to clipboard.' }), 'success');

        } catch {

            createToast(formatMessage({ id: 'failed_to_copy_to_clipboard.9c57abcd', defaultMessage: 'Failed to copy to clipboard.' }), 'danger');

        }

    };

    const showCredentialsDialog = (settings: ConsumerSettings) => {

        const registrationUrl = settings.AdvantageToolBaseURL && settings.Identifier
            ? `${settings.AdvantageToolBaseURL}${settings.Identifier}`
            : '';

        credentialsDialogRef.current?.create(
            'LTI Credentials',
            <div>
                <p className="mb-3 text-muted">
                    <FormattedMessage defaultMessage="Your LTI Tool Consumer credentials are ready." id="your_lti_tool_consumer_credentials_are_r.6413edde" />
                </p>
                <div className="border rounded p-3 mb-3 bg-light">
                    <h6 className="mb-2">
                        <FormattedMessage defaultMessage="LTI 1.1" id="lti_11.dac570eb" />
                    </h6>
                    <div className="mb-1">
                        <strong>
                            <FormattedMessage defaultMessage="Key:" id="key.2998634d" />
                        </strong>
                        {' '}
                        <span className="text-break">
                            {settings.ConsumerKey}
                        </span>
                    </div>
                    <div>
                        <strong>
                            <FormattedMessage defaultMessage="Secret:" id="secret.a82acff0" />
                        </strong>
                        {' '}
                        <span className="text-break">
                            {settings.ConsumerSecret}
                        </span>
                    </div>
                </div>
                <div className="border rounded p-3 bg-light">
                    <h6 className="mb-2">
                        <FormattedMessage defaultMessage="LTI Advantage" id="lti_advantage.054af74b" />
                    </h6>
                    <div>
                        <strong>
                            <FormattedMessage defaultMessage="Dynamic Registration URL:" id="dynamic_registration_url.09d6eb01" />
                        </strong>
                        {registrationUrl ? (
                            <button
                                className="btn btn-link p-0 text-start text-break align-baseline"
                                onClick={() => copyToClipboard(registrationUrl)}
                                style={{ textDecoration: 'underline' }}
                                type="button"
                            >
                                {registrationUrl}
                            </button>
                        ) : (
                            <span className="text-muted">
                                {' '}
                                <FormattedMessage defaultMessage="Not available yet — contact GMetrix support to provision LTI 1.3." id="not_available_yet_contact_gmetrix_suppor.c308412f" />
                            </span>
                        )}
                    </div>
                </div>
            </div>,
            {
                buttonProps: {
                    buttonText: formatMessage({ id: 'close.99e01fb5', defaultMessage: 'Close' }),
                    hideCancel: true,
                    variant: 'primary'
                },
                size: 'lg'
            }
        );

    };

    const loadSettings = async () => {

        try {

            setState({ loadingSettings: true });

            const settings = await PartnerApi.GetConsumerSettingsByPartnerId(partnerId);

            setState({ consumerSettings: settings, loadingSettings: false });

        } catch {

            setState({ loadingSettings: false });

        }

    };

    useEffect(() => {

        if (!partnerId) return;
        loadSettings();

    }, [partnerId]);

    const handleRequestKey = async () => {

        try {

            setState({ requestingKey: true });

            const result = await PartnerApi.CreateConsumserSettingsByPartnerId(partnerId);

            setState({
                consumerSettings: result,
                requestingKey: false
            });

            if (result) {

                showCredentialsDialog(result);

            }

        } catch {

            setState({ requestingKey: false });

        }

    };

    return (
        <DefaultLayout pageTitle={formatMessage({ id: 'lti_resource_keys.18a6c604', defaultMessage: 'LTI Resource Keys' })}>
            <div className="lti-resource-keys d-flex flex-column gap-3">
                <PageHeader
                    end={(
                        <>
                            {!consumerSettings && !loadingSettings && (
                                <GMetrixButton
                                    disabled={requestingKey}
                                    onClick={handleRequestKey}
                                    variant='primary'
                                >
                                    {requestingKey ? <FormattedMessage defaultMessage="Requesting…" id="requesting.22c2782b" /> : <FormattedMessage defaultMessage="Request LTI Key and Secret" id="request_lti_key_and_secret.42022008" />}
                                </GMetrixButton>
                            )}

                            {consumerSettings && (
                                <GMetrixButton
                                    onClick={() => showCredentialsDialog(consumerSettings)}
                                    variant='primary'
                                >
                                    <FormattedMessage defaultMessage="View LTI Credentials" id="view_lti_credentials.40518e35" />
                                </GMetrixButton>
                            )}
                        </>
                    )}
                    helpContent={helpContent}
                    title={formatMessage({ id: 'lti_resource_keys.18a6c604', defaultMessage: 'LTI Resource Keys' })}
                />

                <ResourceKeyBrowser mode="lookup" partnerId={partnerId} />

                <GMetrixDialogShell ref={credentialsDialogRef} />
            </div>
        </DefaultLayout>
    );

};
